// Performance utilities for Core Web Vitals optimization

/**
 * Preload critical resources
 * Call this in App.js or index.js for critical above-the-fold content
 */
export const preloadCriticalResources = () => {
  // Preload hero images
  const heroImages = [
    '/logo-white.png',
  ];

  heroImages.forEach(src => {
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    link.href = src;
    document.head.appendChild(link);
  });

  // Preconnect to image CDNs
  const preconnectUrls = [
    'https://images.unsplash.com',
    'https://fonts.googleapis.com',
    'https://fonts.gstatic.com',
  ];

  preconnectUrls.forEach(url => {
    const existing = document.querySelector(`link[href="${url}"]`);
    if (!existing) {
      const link = document.createElement('link');
      link.rel = 'preconnect';
      link.href = url;
      link.crossOrigin = 'anonymous';
      document.head.appendChild(link);
    }
  });
};

/**
 * Generate optimized image URL for Unsplash
 * Supports WebP, quality adjustment, and responsive sizes
 */
export const optimizeUnsplashUrl = (url, options = {}) => {
  if (!url || !url.includes('unsplash.com')) return url;

  const {
    width = 800,
    quality = 75,
    format = 'auto', // auto will serve WebP to supported browsers
  } = options;

  const baseUrl = url.split('?')[0];
  const params = new URLSearchParams({
    w: width.toString(),
    q: quality.toString(),
    fm: format,
    fit: 'crop',
    auto: 'format,compress',
  });

  return `${baseUrl}?${params.toString()}`;
};

/**
 * Generate srcset for responsive images
 */
export const generateSrcSet = (url, widths = [400, 800, 1200, 1600]) => {
  if (!url || !url.includes('unsplash.com')) return undefined;

  const baseUrl = url.split('?')[0];
  return widths
    .map(w => `${baseUrl}?w=${w}&q=75&auto=format,compress ${w}w`)
    .join(', ');
};

/**
 * Calculate aspect ratio for CLS prevention
 */
export const getAspectRatioPadding = (width, height) => {
  return `${(height / width) * 100}%`;
};

/**
 * Web Vitals metrics tracking
 * Integrates with GA4 for performance monitoring
 */
export const trackWebVitals = (metric) => {
  // Log to console in development
  if (process.env.NODE_ENV === 'development') {
    console.log(`[Web Vitals] ${metric.name}: ${metric.value.toFixed(2)}ms`);
  }

  // Send to Google Analytics 4
  if (window.gtag) {
    window.gtag('event', metric.name, {
      event_category: 'Web Vitals',
      event_label: metric.id,
      value: Math.round(metric.name === 'CLS' ? metric.value * 1000 : metric.value),
      non_interaction: true,
    });
  }
};

/**
 * Initialize Web Vitals monitoring
 * Call this in index.js
 */
export const initWebVitals = async () => {
  if (typeof window !== 'undefined') {
    try {
      // web-vitals v5+ uses onINP instead of onFID
      const { onCLS, onINP, onLCP, onFCP, onTTFB } = await import('web-vitals');
      
      onCLS(trackWebVitals);
      onINP(trackWebVitals);  // Interaction to Next Paint (replaced FID)
      onLCP(trackWebVitals);
      onFCP(trackWebVitals);
      onTTFB(trackWebVitals);
    } catch (e) {
      console.warn('Web Vitals not available:', e);
    }
  }
};

/**
 * Defer non-critical JavaScript
 */
export const deferNonCritical = (callback, delay = 0) => {
  if ('requestIdleCallback' in window) {
    window.requestIdleCallback(callback, { timeout: delay + 2000 });
  } else {
    setTimeout(callback, delay);
  }
};

/**
 * Prefetch page for faster navigation
 */
export const prefetchPage = (href) => {
  if (!href || typeof document === 'undefined') return;
  
  const existing = document.querySelector(`link[href="${href}"]`);
  if (existing) return;

  const link = document.createElement('link');
  link.rel = 'prefetch';
  link.href = href;
  document.head.appendChild(link);
};

/**
 * Check if user prefers reduced motion
 */
export const prefersReducedMotion = () => {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
};

/**
 * Intersection Observer hook for lazy loading
 */
export const createLazyLoadObserver = (callback, options = {}) => {
  const defaultOptions = {
    root: null,
    rootMargin: '200px',
    threshold: 0,
    ...options,
  };

  return new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        callback(entry.target);
      }
    });
  }, defaultOptions);
};

export default {
  preloadCriticalResources,
  optimizeUnsplashUrl,
  generateSrcSet,
  getAspectRatioPadding,
  trackWebVitals,
  initWebVitals,
  deferNonCritical,
  prefetchPage,
  prefersReducedMotion,
  createLazyLoadObserver,
};
